/**
 * Справочные данные модуля MWS.
 *
 * Источник — doc/README.MD §2 (справочники). Эти массивы используются
 * при идемпотентном сидинге в server/setup.ts. Менять состав здесь —
 * после старта значения доедут до БД через upsert по стабильному ключу.
 */

/**
 * SCHEDULE_STATUS — статус ячейки матрицы «секция × этап» (ScheduleEntry).
 * Денормализованное поле; «просрочено» зависит от «сегодня», поэтому
 * пересчитывается в трёх точках: ingest версии RP, приёмка (M3), ежедневный cron.
 * Формула — единая, в services/recomputeScheduleStatus. ПОРЯДОК неважен, но
 * значение по умолчанию — 'в плане'.
 */
export const MWS_SCHEDULE_STATUS = {
  PLANNED: 'в плане',
  IN_PROGRESS: 'в работе',
  DONE: 'выполнено',
  OVERDUE: 'просрочено',
} as const;
export const MWS_SCHEDULE_STATUSES = [
  MWS_SCHEDULE_STATUS.PLANNED,
  MWS_SCHEDULE_STATUS.IN_PROGRESS,
  MWS_SCHEDULE_STATUS.DONE,
  MWS_SCHEDULE_STATUS.OVERDUE,
] as const;
export type MwsScheduleStatus = (typeof MWS_SCHEDULE_STATUSES)[number];

/**
 * M3 — инспекции и несоответствия (enum-коды, см. doc/README.MD §2).
 * status — жизненный цикл инспекции; result — исход (только при «выполнена»);
 * origin — плановая (из заявки) / внеплановая. Отмена = status, НЕ result.
 *
 * ВАЖНО: в логике (сравнения, дефолты, значения для create/update) используем
 * ИМЕНОВАННЫЕ члены (MWS_INSPECTION_RESULT.ACCEPTED), а НЕ строковые литералы.
 * Тип колонки из EnumField — `string` (см. field-types.ts), поэтому опечатку в
 * литерале компилятор не ловит, а член объекта — ловит. Массивы *_STATUSES
 * выводятся отсюда, литерал каждой строки объявлен один раз.
 */
export const MWS_INSPECTION_STATUS = {
  PLANNED: 'запланирована',
  /** Вошла в заявку, отправленную верфи (submitWeekRequest). «Заявка недели» = плановые+заявленные инспекции недели. */
  REQUESTED: 'заявлена',
  DONE: 'выполнена',
  CANCELLED: 'отменена',
} as const;
export const MWS_INSPECTION_STATUSES = [
  MWS_INSPECTION_STATUS.PLANNED,
  MWS_INSPECTION_STATUS.REQUESTED,
  MWS_INSPECTION_STATUS.DONE,
  MWS_INSPECTION_STATUS.CANCELLED,
] as const;
export type MwsInspectionStatus = (typeof MWS_INSPECTION_STATUSES)[number];

export const MWS_INSPECTION_RESULT = {
  ACCEPTED: 'принято',
  REJECTED: 'не принято',
} as const;
export const MWS_INSPECTION_RESULTS = [
  MWS_INSPECTION_RESULT.ACCEPTED,
  MWS_INSPECTION_RESULT.REJECTED,
] as const;
export type MwsInspectionResult = (typeof MWS_INSPECTION_RESULTS)[number];

/**
 * Причина отмены захода (status=отменена).
 *
 * Категория, а не свободный текст: по ней считают, сколько заходов сорвала
 * верфь за период. Текст комментария при этом берётся из справочника фраз
 * (`mws_inspection_templates`, ключи `cancel_yard`/`cancel_weather`) — там РГН
 * правит формулировку без деплоя, здесь живёт только устойчивая категория.
 */
export const MWS_CANCEL_REASON = {
  /** Верфь отменила предъявление работ. */
  SHIPYARD: 'верфь',
  /** Приёмка не состоялась из-за погоды. */
  WEATHER: 'погода',
} as const;
export const MWS_CANCEL_REASONS = [
  MWS_CANCEL_REASON.SHIPYARD,
  MWS_CANCEL_REASON.WEATHER,
] as const;
export type MwsCancelReason = (typeof MWS_CANCEL_REASONS)[number];

/** Ключ системной фразы-комментария для причины отмены (справочник фраз, группа cancel). */
export const MWS_CANCEL_REASON_PHRASE_KEY: Record<MwsCancelReason, string> = {
  [MWS_CANCEL_REASON.SHIPYARD]: 'cancel_yard',
  [MWS_CANCEL_REASON.WEATHER]: 'cancel_weather',
};

export const MWS_INSPECTION_ORIGIN = {
  PLANNED: 'плановая',
  UNPLANNED: 'внеплановая',
  /** Повторная инспекция ранее проверенного объекта (связана с родителем через parent_inspection_id). */
  REINSPECTION: 'реинспекция',
  /** Обход судна без заявки верфи — фиксация того, что попалось на глаза. */
  PATROL: 'патрулинг',
} as const;
export const MWS_INSPECTION_ORIGINS = [
  MWS_INSPECTION_ORIGIN.PLANNED,
  MWS_INSPECTION_ORIGIN.UNPLANNED,
  MWS_INSPECTION_ORIGIN.REINSPECTION,
  MWS_INSPECTION_ORIGIN.PATROL,
] as const;
export type MwsInspectionOrigin = (typeof MWS_INSPECTION_ORIGINS)[number];

/**
 * Группа (категория) фразы-шаблона примечания инспекции — колонка group в
 * mws_inspection_templates. Управляет тем, под каким заголовком-рядом фраза
 * показывается в модалке приёмки. Порядок массива = порядок рядов в UI.
 *  - MAIN        — основные фразы вида работ (с плейсхолдерами);
 *  - RESULT      — исход приёмки («Без замечаний», «Не принято»…);
 *  - ACCEPTOR    — кем принято («Принято РС», «Принято Hilong»);
 *  - CANCEL      — отмена/перенос приёмки;
 *  - DOCS        — ссылки на протоколы и акты инспекции (ряд «Протоколы, Акты»);
 *  - NCR_ACTIONS — «Предпринятые действия» NCR (ряд в форме несоответствия, не в приёмке).
 * Подписи рядов задаются в JSX модалки (не здесь).
 */
export const MWS_INSPECTION_TEMPLATE_GROUP = {
  MAIN: 'main',
  RESULT: 'result',
  ACCEPTOR: 'acceptor',
  CANCEL: 'cancel',
  DOCS: 'docs',
  NCR_ACTIONS: 'ncr_actions',
} as const;
export const MWS_INSPECTION_TEMPLATE_GROUPS = [
  MWS_INSPECTION_TEMPLATE_GROUP.MAIN,
  MWS_INSPECTION_TEMPLATE_GROUP.RESULT,
  MWS_INSPECTION_TEMPLATE_GROUP.ACCEPTOR,
  MWS_INSPECTION_TEMPLATE_GROUP.CANCEL,
  MWS_INSPECTION_TEMPLATE_GROUP.DOCS,
  MWS_INSPECTION_TEMPLATE_GROUP.NCR_ACTIONS,
] as const;
export type MwsInspectionTemplateGroup = (typeof MWS_INSPECTION_TEMPLATE_GROUPS)[number];
/** Ряды-группы модалки ПРИЁМКИ инспекции (без ncr_actions — тот живёт в форме NCR). */
export const MWS_INSPECTION_ACCEPT_GROUPS = [
  MWS_INSPECTION_TEMPLATE_GROUP.MAIN,
  MWS_INSPECTION_TEMPLATE_GROUP.RESULT,
  MWS_INSPECTION_TEMPLATE_GROUP.ACCEPTOR,
  MWS_INSPECTION_TEMPLATE_GROUP.CANCEL,
] as const;
export type MwsInspectionAcceptGroup = (typeof MWS_INSPECTION_ACCEPT_GROUPS)[number];

/**
 * Время суток инспекции (AM/PM из заявки верфи) — enum-колонка time_of_day.
 * Единый источник для схемы, формы, сортировки в отчёте и сидов.
 */
export const MWS_TIME_OF_DAY = {
  AM: 'AM',
  PM: 'PM',
} as const;
export const MWS_TIME_OF_DAYS = [MWS_TIME_OF_DAY.AM, MWS_TIME_OF_DAY.PM] as const;
export type MwsTimeOfDay = (typeof MWS_TIME_OF_DAYS)[number];

export const MWS_NCR_SEVERITY = {
  CRITICAL: 'критичное',
  MAJOR: 'значительное',
  MINOR: 'малозначительное',
} as const;
export const MWS_NCR_SEVERITIES = [
  MWS_NCR_SEVERITY.CRITICAL,
  MWS_NCR_SEVERITY.MAJOR,
  MWS_NCR_SEVERITY.MINOR,
] as const;
export type MwsNcrSeverity = (typeof MWS_NCR_SEVERITIES)[number];

export const MWS_NCR_STATUS = {
  OPEN: 'открыт',
  CLOSED: 'закрыт',
} as const;
export const MWS_NCR_STATUSES = [MWS_NCR_STATUS.OPEN, MWS_NCR_STATUS.CLOSED] as const;
export type MwsNcrStatus = (typeof MWS_NCR_STATUSES)[number];

/**
 * Вид записи: «замечание» (фиксируется, но может не попасть в формальный перечень)
 * vs «NCR» (формальное несоответствие). Ортогонально статусу (жизненный цикл) и
 * критичности. Дефолт — замечание (часть из них эскалируется в NCR).
 */
export const MWS_NCR_KIND = {
  REMARK: 'Замечание',
  NCR: 'NCR',
} as const;
export const MWS_NCR_KINDS = [MWS_NCR_KIND.REMARK, MWS_NCR_KIND.NCR] as const;
export type MwsNcrKind = (typeof MWS_NCR_KINDS)[number];

/**
 * Результат акта приёмки (пишут от руки на англ. на присланном верфью акте).
 * ПРОВИЗОРНО — состав значений подтверждается заказчиком; поле nullable.
 */
export const MWS_ACT_RESULT = {
  NO_REMARKS: 'без замечаний',
  WITH_REMARKS: 'с замечаниями',
  REJECTED: 'не принято',
} as const;
export const MWS_ACT_RESULTS = [
  MWS_ACT_RESULT.NO_REMARKS,
  MWS_ACT_RESULT.WITH_REMARKS,
  MWS_ACT_RESULT.REJECTED,
] as const;
export type MwsActResult = (typeof MWS_ACT_RESULTS)[number];

/**
 * Тип документа электрической системы (по реестру КД QSJY4260-600-03TM).
 * Различает рабочие чертежи (系统图/布置图), к которым верфь адресует инспекции,
 * и офисные документы (расчёты/перечни/регламент/каталог). Значения — латиницей
 * (технический классификатор); русские подписи — на UI.
 */
export const MWS_ELECTRICAL_SYSTEM_KIND = {
  DIAGRAM: 'diagram', // 系统图 — системная схема
  LAYOUT: 'layout', // 布置图 — компоновочный чертёж
  CALCULATION: 'calculation', // 计算书 — расчётная книга
  LIST: 'list', // 明细表 — перечень
  RULE: 'rule', // 工艺准则 — техрегламент
  CATALOG: 'catalog', // 目录 — каталог документов
} as const;
export const MWS_ELECTRICAL_SYSTEM_KINDS = [
  MWS_ELECTRICAL_SYSTEM_KIND.DIAGRAM,
  MWS_ELECTRICAL_SYSTEM_KIND.LAYOUT,
  MWS_ELECTRICAL_SYSTEM_KIND.CALCULATION,
  MWS_ELECTRICAL_SYSTEM_KIND.LIST,
  MWS_ELECTRICAL_SYSTEM_KIND.RULE,
  MWS_ELECTRICAL_SYSTEM_KIND.CATALOG,
] as const;
export type MwsElectricalSystemKind = (typeof MWS_ELECTRICAL_SYSTEM_KINDS)[number];

/**
 * Борт судна (船舷) — сторона размещения конца кабеля / оборудования.
 * P — левый (port), S — правый (starboard). Значения из кабельного журнала.
 */
export const MWS_SIDE = {
  PORT: 'P',
  STARBOARD: 'S',
} as const;
export const MWS_SIDES = [MWS_SIDE.PORT, MWS_SIDE.STARBOARD] as const;
export type MwsSide = (typeof MWS_SIDES)[number];

export const MWS_REPORT_TYPE = {
  WEEKLY: 'еженедельный',
  MONTHLY: 'ежемесячный',
  QUARTERLY: 'ежеквартальный',
} as const;
export const MWS_REPORT_TYPES = [
  MWS_REPORT_TYPE.WEEKLY,
  MWS_REPORT_TYPE.MONTHLY,
  MWS_REPORT_TYPE.QUARTERLY,
] as const;
export type MwsReportType = (typeof MWS_REPORT_TYPES)[number];

export const MWS_REPORT_STATUS = {
  DRAFT: 'черновик',
  FINAL: 'финальный',
} as const;
export const MWS_REPORT_STATUSES = [MWS_REPORT_STATUS.DRAFT, MWS_REPORT_STATUS.FINAL] as const;
export type MwsReportStatus = (typeof MWS_REPORT_STATUSES)[number];

/**
 * Маппинг колонок Rolling Plan (как их отдаёт парсер) → 11 этапов STAGE.
 * Делаем маппинг У СЕБЯ (normalizeRollingPlan), парсер отдаёт сырые колонки.
 * Каждый этап: position (1..11) + begin/end колонки исходной строки.
 * Этапы 8/9 — в PDF один блок «erection»: 8 = укрупнение (erection_start→erection_end),
 * 9 = насыщение блока (begin отсутствует в PDF → берём erection_end, end = outfit_end).
 * Этап 11 (стапель) — единственная дата dock_date (begin отсутствует).
 * Значения ячеек: 'OK' (готово, даты нет), '—' (неприменимо), '' (нет данных).
 */
export const MWS_RP_STAGE_COLUMNS: ReadonlyArray<{
  position: number;
  begin_col: string | null;
  end_col: string;
}> = [
  { position: 1, begin_col: 'cut_start', end_col: 'cut_end' },
  { position: 2, begin_col: 'subasm_start', end_col: 'subasm_end' },
  { position: 3, begin_col: 'struct_in_on', end_col: 'struct_in_off' },
  { position: 4, begin_col: 'struct_out_start', end_col: 'struct_out_end' },
  { position: 5, begin_col: 'prefit_start', end_col: 'prefit_end' },
  { position: 6, begin_col: 'blast_start', end_col: 'blast_end' },
  { position: 7, begin_col: 'paint_start', end_col: 'paint_end' },
  { position: 8, begin_col: 'erection_start', end_col: 'erection_end' },
  { position: 9, begin_col: 'erection_end', end_col: 'outfit_end' },
  { position: 10, begin_col: 'erect_paint_start', end_col: 'erect_paint_end' },
  { position: 11, begin_col: null, end_col: 'dock_date' },
] as const;

/**
 * Пороги риск-светофора объёмов (вкладка «Объёмы», режим «Риск»).
 * Риск = запас до стапеля (dock_date − planned_end) мал И сокращается между
 * версиями RP. Единый тюнингуемый источник (services/volumeMetrics его берёт по
 * умолчанию). Дни — целые; criticalShare/warnShare — доли тоннажа группы (0..1).
 */
export const MWS_VOLUME_RISK_THRESHOLDS = {
  /** Запас ≤ этого — критически мал (дни). */
  slackRed: 3,
  /** Запас ≤ этого — мал, «внимание» (дни). */
  slackWarn: 10,
  /** Сокращение запаса до стапеля между версиями ≥ этого — значимо (дни). */
  erosionWarn: 7,
  /** Доля критичного тоннажа в группе → светофор 🔴. */
  criticalShare: 0.05,
  /** Доля (критич.+внимание) тоннажа → светофор 🟡. */
  warnShare: 0.15,
};

// STAGE (этапы секции), AREA_CODE (области корпуса) — это ДАННЫЕ справочника,
// живут в data/reference.json (сидятся seed:reference / boot, create-if-missing).
// Здесь — только символы кода (коды направлений/категорий ниже).

/**
 * DIRECTION — направления работ (4; разделы отчёта).
 * `code` — стабильный ключ для привязки WorkType → Direction при сидинге
 * (имя редактируемо, код — нет).
 */
/** Коды направлений — стабильные ключи (единый источник для сидинга и UI). */
export const MWS_DIRECTION_CODE = {
  HULL: 'HULL',
  MECH: 'MECH',
  PAINT: 'PAINT',
  ELEC: 'ELEC',
} as const;
/** Код направления — производный тип из MWS_DIRECTION_CODE (без дублей-литералов). */
export type MwsDirectionCode = (typeof MWS_DIRECTION_CODE)[keyof typeof MWS_DIRECTION_CODE];
// Имена направлений (DIRECTION) — данные, в data/reference.json (directions[]).

/**
 * WORK_CATEGORY — категории видов работ (пополняемый справочник).
 * `code` — стабильный ключ для привязки WorkType → WorkCategory при сидинге.
 */
/**
 * Коды категорий работ — стабильные ключи (единый источник для сидинга и UI).
 * НК и ИСПЫТАНИЯ — это и есть «тип протокола»: метод протокола лежит в одной из
 * этих категорий, тип выводится из категории метода (отдельного поля kind нет).
 */
export const MWS_WORK_CATEGORY_CODE = {
  PRODUCTION: 'производство',
  NDT: 'НК',
  TESTING: 'испытания',
  EQUIPMENT: 'оборудование',
  ROUNDS: 'обход',
} as const;

/** Категории-«типы протокола» — методы протокола берутся только из них. */
export const MWS_PROTOCOL_CATEGORY_CODES = [
  MWS_WORK_CATEGORY_CODE.NDT,
  MWS_WORK_CATEGORY_CODE.TESTING,
] as const;

// Имена категорий (WORK_CATEGORY) и виды работ (WORK_TYPE) — данные,
// в data/reference.json (work_categories[]/work_types[]; work_types ключуются по
// direction_code/category_code, чьи символы — выше).

/**
 * OBJECT_CLASS пункта ITP — корпусной (применяется к секциям) или оборудование
 * (само себе объект контроля). Загрузчик выводит из раздела ITP: раздел I
 * (Корпус) → секция, разделы II–VI → оборудование.
 * Колонка mws_itp_items.object_class; здесь — символ кода (значения enum).
 */
export const MWS_ITP_OBJECT_CLASS = {
  SECTION: 'секция',
  EQUIPMENT: 'оборудование',
} as const;
export const MWS_ITP_OBJECT_CLASSES = [
  MWS_ITP_OBJECT_CLASS.SECTION,
  MWS_ITP_OBJECT_CLASS.EQUIPMENT,
] as const;
export type MwsItpObjectClass = (typeof MWS_ITP_OBJECT_CLASS)[keyof typeof MWS_ITP_OBJECT_CLASS];

/**
 * Тип механической единицы учёта — СПРАВОЧНИК mws_mech_entity_type (не enum):
 * танк (замкнутый объём, испытания герметичность/прочность) vs оборудование
 * (машинная единица — FAT/наладка/сертификат: вал, винт, руль, ДГ, насос).
 * Пополняемый домен-командой (напр. «Помещение», «Арматура») без деплоя.
 *
 * Здесь — только стабильные КОДЫ-якоря (сидинг + ветвление UI/отчёта по code,
 * как MWS_DIRECTION_CODE); имена — данные в data/reference.json (mech_entity_types[]).
 * НЕ путать с MWS_ITP_OBJECT_CLASS (секция/оборудование — классы пунктов ITP).
 */
export const MWS_MECH_ENTITY_TYPE_CODE = {
  TANK: 'TANK',
  EQUIPMENT: 'EQUIPMENT',
  HATCH_COVER: 'HATCH_COVER',
} as const;
export type MwsMechEntityTypeCode =
  (typeof MWS_MECH_ENTITY_TYPE_CODE)[keyof typeof MWS_MECH_ENTITY_TYPE_CODE];

/**
 * Класс места трубы-спула (unit_code у mws_piping_spools) — в托盘表 верфи трубы
 * адресованы тремя разными видами мест, и различить их можно ТОЛЬКО по имени файла
 * чертежа (внутри таблицы у всех метка 分段名):
 *   SECTION — строительная секция (分段), напр. «1004» → каталог mws_sections;
 *   AREA    — зона достройки (区域), напр. «PDK01»/«PDE01A» — в каталоге секций
 *             отсутствует (это другая пространственная ось, 5565 труб серии);
 *   OTHER   — вне обеих осей: «MPYG» (排烟管, дымоход).
 * Значения выведены из всех 134 чертежей 01T серии QSJY4260.
 */
export const MWS_PIPING_UNIT_KIND = {
  SECTION: 'section',
  AREA: 'area',
  OTHER: 'other',
} as const;
export const MWS_PIPING_UNIT_KINDS = [
  MWS_PIPING_UNIT_KIND.SECTION,
  MWS_PIPING_UNIT_KIND.AREA,
  MWS_PIPING_UNIT_KIND.OTHER,
] as const;
export type MwsPipingUnitKind = (typeof MWS_PIPING_UNIT_KINDS)[number];

/**
 * Ось объекта вида работ — к какому классу объектов инспекции применим work_type
 * (флаги is_section/is_electric_system/is_piping_system/is_mech_entity).
 * Используется формой инспекции для фильтрации списка видов работ по режиму
 * (WorkTypeRepository.filterByInspectionObject). Вид работ БЕЗ флагов —
 * универсальный: виден в любом режиме (работы «без объекта» и неразмеченный legacy).
 */
export const MWS_WORK_TYPE_AXIS = {
  SECTION: 'section',
  ELECTRIC: 'electric',
  PIPING: 'piping',
  MECH: 'mech',
} as const;
export type MwsWorkTypeAxis = (typeof MWS_WORK_TYPE_AXIS)[keyof typeof MWS_WORK_TYPE_AXIS];
export const MWS_WORK_TYPE_AXES = [
  MWS_WORK_TYPE_AXIS.SECTION,
  MWS_WORK_TYPE_AXIS.ELECTRIC,
  MWS_WORK_TYPE_AXIS.PIPING,
  MWS_WORK_TYPE_AXIS.MECH,
] as const;

/**
 * Русские подписи осей — специализация инспекции в UI (переключатель формы,
 * колонка и фильтр списка). Держим рядом со значениями: подпись «Корпус» у оси
 * `section` — та же, что в переключателе формы, расходиться им нельзя.
 */
export const MWS_WORK_TYPE_AXIS_LABEL: Record<MwsWorkTypeAxis, string> = {
  [MWS_WORK_TYPE_AXIS.SECTION]: 'Корпус',
  [MWS_WORK_TYPE_AXIS.ELECTRIC]: 'Электрика',
  [MWS_WORK_TYPE_AXIS.PIPING]: 'Трубопроводы',
  [MWS_WORK_TYPE_AXIS.MECH]: 'Оборудование/Цистерны',
};

/**
 * Псевдо-ось «универсальный» (ни одного флага применимости) — значение фильтра
 * грида видов работ (WorkTypeRepository.filterByObjectAxis); осью формы не является.
 */
export const MWS_WORK_TYPE_AXIS_UNIVERSAL = 'universal';

// Тип проекта (серия) и корпуса — это «конфиг инстанса», НЕ константы кода.
// Стартовые данные живут в data/project.json и заливаются разово через
// `npm run seed:project` (create-if-missing). Источник истины — БД.

/**
 * Роли модуля живут в `common/roles.ts` (там же их определения для identity
 * и карта прав рядом, в `common/permissions.ts`). Здесь — ре-экспорт для
 * совместимости: `MWS_SURVEYOR_ROLE` импортируется из `constants` в сидах,
 * API, тестах и клиентских пикерах.
 */
export { MWS_SURVEYOR_ROLE } from './common/roles';

/**
 * Категории файлов отчётной недели (filemanager: defineFileCategory).
 * Регистрируются идемпотентно в setup.ts со скоупом entity_type = ReportingWeek.
 * Состав — по реальной «папке недели» верфи: заявки на инспекцию по дням (xls),
 * подписанные акты UT/PT/VT (pdf), планы на неделю, канбан верфи,
 * прочее (КТД/сертификаты — до появления сущности оборудования, вопрос 7.10).
 */
export const MWS_WEEK_FILE_CATEGORY = {
  REQUEST: 'MWS_WEEK_REQUEST',
  KTD: 'MWS_WEEK_KTD',
  PLAN: 'MWS_WEEK_PLAN',
  ROLLING_PLAN: 'MWS_WEEK_ROLLING_PLAN',
  KANBAN: 'MWS_WEEK_KANBAN',
  OTHER: 'MWS_WEEK_OTHER',
  /**
   * Сформированный ZIP-архив «папки недели». Сама выгрузка (mws::exportWeekArchive)
   * снесена — источником выдачи стало дерево Диска, — но категория остаётся:
   * ранее сформированные архивы лежат на Диске, и раскладка по ней их узнаёт,
   * чтобы не затягивать прошлый ZIP внутрь новой выгрузки папки.
   */
  WEEKLY_ARCHIVE: 'MWS_WEEK_ARCHIVE',
} as const;
// Акт приёмки — отдельная сущность (mws_acceptance_acts), не файловая категория
// недели (убрана, чтобы не дублировать ввод). Акты заводятся через экран/таб «Акты».
// КТД — конструкторско-технологическая документация (чертежи, спецификации); в архиве
// недели — фиксированный раздел «05_КТД».
export const MWS_WEEK_FILE_CATEGORIES = [
  { slug: MWS_WEEK_FILE_CATEGORY.REQUEST, name: 'Заявка на инспекцию', sortOrder: 1 },
  { slug: MWS_WEEK_FILE_CATEGORY.KTD, name: 'КТД', sortOrder: 2 },
  { slug: MWS_WEEK_FILE_CATEGORY.PLAN, name: 'План на неделю', sortOrder: 3 },
  { slug: MWS_WEEK_FILE_CATEGORY.ROLLING_PLAN, name: 'Rolling Plan', sortOrder: 4 },
  { slug: MWS_WEEK_FILE_CATEGORY.KANBAN, name: 'Канбан верфи', sortOrder: 5 },
  { slug: MWS_WEEK_FILE_CATEGORY.OTHER, name: 'Прочее', sortOrder: 6 },
  { slug: MWS_WEEK_FILE_CATEGORY.WEEKLY_ARCHIVE, name: 'Сформированный архив', sortOrder: 7 },
] as const;

/**
 * Категории вложений ИНСПЕКЦИИ (скоуп entity_type = Inspection).
 * Только фото с осмотра (галерея). Бывшая категория «Акт приёмки (UT/PT/VT)» убрана:
 * к инспекции относятся ПРОТОКОЛЫ (отдельная сущность mws_protocols, вкладка
 * «Протоколы»), а не акты — акты живут на неделе как сущность mws_acceptance_acts.
 */
/**
 * Статусы файла заявки (mws_request_imports): «загружен» (строка создаётся
 * сразу при загрузке файла, registerWeekRequestFiles) → «обработка» (агент
 * работает) → «проанализирован» (rows в журнале, инспекции НЕ созданы — ждёт
 * подтверждения в модалке предпросмотра) → «готово» (инспекции созданы,
 * applyRequestImport) либо «ошибка» (error, можно перезапустить анализ).
 * Анализ доступен для «загружен»/«ошибка»; «проанализирован» открывает модалку
 * из сохранённых rows без повторного LLM; «готово» повторно не анализируется.
 */
export const MWS_REQUEST_IMPORT_STATUS = {
  UPLOADED: 'загружен',
  PROCESSING: 'обработка',
  PARSED: 'проанализирован',
  DONE: 'готово',
  FAILED: 'ошибка',
} as const;
export const MWS_REQUEST_IMPORT_STATUSES = [
  MWS_REQUEST_IMPORT_STATUS.UPLOADED,
  MWS_REQUEST_IMPORT_STATUS.PROCESSING,
  MWS_REQUEST_IMPORT_STATUS.PARSED,
  MWS_REQUEST_IMPORT_STATUS.DONE,
  MWS_REQUEST_IMPORT_STATUS.FAILED,
] as const;
export type MwsRequestImportStatus = (typeof MWS_REQUEST_IMPORT_STATUSES)[number];

/**
 * Статусы файла плана верфи (mws_plan_imports): «загружен» (строка создаётся
 * сразу при загрузке, registerWeekPlanFiles) → «обработка» → «готово» (rows —
 * распарсенные строки плана, только отображение) либо «ошибка» (перезапуск).
 * Фазы подтверждения нет — план ничего не создаёт, сюрвейер только смотрит.
 */
export const MWS_PLAN_IMPORT_STATUS = {
  UPLOADED: 'загружен',
  PROCESSING: 'обработка',
  DONE: 'готово',
  FAILED: 'ошибка',
} as const;
export const MWS_PLAN_IMPORT_STATUSES = [
  MWS_PLAN_IMPORT_STATUS.UPLOADED,
  MWS_PLAN_IMPORT_STATUS.PROCESSING,
  MWS_PLAN_IMPORT_STATUS.DONE,
  MWS_PLAN_IMPORT_STATUS.FAILED,
] as const;
export type MwsPlanImportStatus = (typeof MWS_PLAN_IMPORT_STATUSES)[number];

/**
 * Статусы файла Rolling Plan (mws_rp_imports) — жизненный цикл идентичен плану
 * верфи («загружен» → «обработка» → «готово»/«ошибка»), значения переиспользуем.
 * Отличие в исполнителе: вместо LLM — детерминированный Python-парсер PDF
 * (parsers/parse_rolling_plan.py), результат уходит в ingestRollingPlanSnapshot
 * (создаётся версия RP + пересчёт матрицы), а не в rows журнала.
 */
export const MWS_RP_IMPORT_STATUS = MWS_PLAN_IMPORT_STATUS;
export const MWS_RP_IMPORT_STATUSES = MWS_PLAN_IMPORT_STATUSES;
export type MwsRpImportStatus = MwsPlanImportStatus;

export const MWS_INSPECTION_FILE_CATEGORY = {
  PHOTO: 'MWS_INSPECTION_PHOTO',
  /** Сертификаты (PDF/сканы): отдельная вкладка + общий раздел недели «06_Сертификаты». */
  CERTIFICATE: 'MWS_INSPECTION_CERTIFICATE',
  /**
   * Конструкторская документация, приложенная к инспекции: чертежи, схемы,
   * инструкции. Цепляется к инспекции (её и предъявляют), а на Диске уходит в
   * раздел недели «05_КТД» — заказчик ждёт КД в файлах недельного отчёта, а не
   * внутри папки конкретной инспекции.
   */
  KD: 'MWS_INSPECTION_KD',
} as const;
export const MWS_INSPECTION_FILE_CATEGORIES = [
  { slug: MWS_INSPECTION_FILE_CATEGORY.PHOTO, name: 'Фото', sortOrder: 1 },
  { slug: MWS_INSPECTION_FILE_CATEGORY.CERTIFICATE, name: 'Сертификаты', sortOrder: 2 },
  { slug: MWS_INSPECTION_FILE_CATEGORY.KD, name: 'КД', sortOrder: 3 },
] as const;

/**
 * Категория файлов СУЩНОСТИ-протокола (скоуп entity_type = mws::ProtocolRepository).
 * Файлы протокола (PDF/сканы) цепляются к карточке протокола (1:N через uploadedFileLnk).
 */
export const MWS_PROTOCOL_FILE_CATEGORY = { DOC: 'MWS_PROTOCOL_DOC' } as const;
export const MWS_PROTOCOL_FILE_CATEGORIES = [
  { slug: MWS_PROTOCOL_FILE_CATEGORY.DOC, name: 'Протокол', sortOrder: 1 },
] as const;

/**
 * Категория файлов СУЩНОСТИ-акта (скоуп entity_type = mws::AcceptanceActRepository).
 * Имя «Акт приёмки» переиспользуем, но слаг новый: defineFileCategory идемпотентна
 * по слагу и слаг 1:1 с одним entityType (существующие MWS_*_ACT — на неделю/инспекцию).
 */
export const MWS_ACT_FILE_CATEGORY = { DOC: 'MWS_ACT_DOC' } as const;
export const MWS_ACT_FILE_CATEGORIES = [
  { slug: MWS_ACT_FILE_CATEGORY.DOC, name: 'Акт приёмки', sortOrder: 1 },
] as const;

/**
 * Действия отчётной недели для проверки прав через `can(action)`.
 *
 * Живёт в общем коде, а не в server/functions: константу читает и клиент (гасит
 * иконку по `can`), а импорт из `server/*` втягивает в браузерный бандл всю
 * серверную цепочку (репозитории → EventBus → node:events) и ломает сборку.
 */
export const MWS_WEEK_ACTION = {
  /** Массовое создание недель задним числом (ensureWeeksRange). */
  BACKFILL_RANGE: 'backfillRange',
} as const;

/**
 * Категория файлов СУЩНОСТИ-сертификата (скоуп entity_type = mws::CertificateRepository).
 *
 * Сертификат — запись с номером и датой (см. schemas/certificate.schema.ts), файлы
 * цепляются к ней. Прежняя категория вложений инспекции MWS_INSPECTION_CERTIFICATE
 * остаётся только для файлов, загруженных до перехода на сущность (2026-08-11).
 */
export const MWS_CERTIFICATE_FILE_CATEGORY = { DOC: 'MWS_CERTIFICATE_DOC' } as const;
export const MWS_CERTIFICATE_FILE_CATEGORIES = [
  { slug: MWS_CERTIFICATE_FILE_CATEGORY.DOC, name: 'Сертификат', sortOrder: 1 },
] as const;

/**
 * Категория файлов NCR (скоуп entity_type = mws::NcrRepository).
 * Сейчас у NCR один сорт файлов — фото несоответствия; категория проставляется
 * обоими путями прилёта (загрузка в карточку и копирование связи с инспекции,
 * linkInspectionFilesToNcr), чтобы признак «что это за документ» был честным на
 * 100% связей — на него вешаются обработчики (см. doc/FILES.md).
 */
export const MWS_NCR_FILE_CATEGORY = { PHOTO: 'MWS_NCR_PHOTO' } as const;
export const MWS_NCR_FILE_CATEGORIES = [
  { slug: MWS_NCR_FILE_CATEGORY.PHOTO, name: 'Фото', sortOrder: 1 },
] as const;

/**
 * Разделы исходного перечня проектных КД (mws_drawings.part_no) — русские лейблы.
 * Нумерация и состав — из документа проектанта «LIST OF PLANS & DOCUMENTS OF
 * DETAIL DESIGN» (GENERAL / HULL OUTFITTING / HULL STRUCTURE / MACHINERY /
 * ELECTRICAL / FINAL); лейблы используются в гриде/форме чертежей.
 */
export const MWS_DRAWING_PART_LABELS: Record<number, string> = {
  1: 'Общая часть',
  2: 'Насыщение корпуса',
  3: 'Корпусные конструкции',
  4: 'Механическая часть',
  5: 'Электрическая часть',
  6: 'Сдаточная часть',
};

// Сюрвейеры (тестовые identity_users) — данные, в data/surveyors.json
// (сидятся seed:reference / boot с ролью MWS_SURVEYOR, create-if-missing по email).

// ---------------------------------------------------------------------------
// Rolling Plan (новый конвейер снапшотов): mws_rp_snapshots / plans / rows /
// translations. Автономен от старого mws_rolling_plan_* (тот не трогаем).
// ---------------------------------------------------------------------------

/** Статусы снапшота RP: журнал = сама таблица снапшотов (как у rp_imports). */
export const MWS_RP_SNAPSHOT_STATUS = MWS_PLAN_IMPORT_STATUS;
export const MWS_RP_SNAPSHOT_STATUSES = MWS_PLAN_IMPORT_STATUSES;
export type MwsRpSnapshotStatus = MwsPlanImportStatus;

/**
 * Типы подпланов Rolling Plan (определяются нормализатором по составу шапки):
 * structural — вехи секций, pipe — изготовление труб, erection — тотал-блоки,
 * outfitting — достроечный план лид-судна, section_matrix — старый формат
 * (широкая матрица секция × этапы).
 */
export const MWS_RP_PLAN_TYPE = {
  STRUCTURAL: 'structural',
  PIPE: 'pipe',
  ERECTION: 'erection',
  OUTFITTING: 'outfitting',
  SECTION_MATRIX: 'section_matrix',
} as const;
export const MWS_RP_PLAN_TYPES = [
  MWS_RP_PLAN_TYPE.STRUCTURAL,
  MWS_RP_PLAN_TYPE.PIPE,
  MWS_RP_PLAN_TYPE.ERECTION,
  MWS_RP_PLAN_TYPE.OUTFITTING,
  MWS_RP_PLAN_TYPE.SECTION_MATRIX,
] as const;
export type MwsRpPlanType = (typeof MWS_RP_PLAN_TYPES)[number];

/** Источник перевода в словаре mws_rp_translations. */
export const MWS_RP_TRANSLATION_SOURCE = {
  DEEPSEEK: 'deepseek',
  CATALOG: 'catalog',
  MANUAL: 'manual',
} as const;
export const MWS_RP_TRANSLATION_SOURCES = [
  MWS_RP_TRANSLATION_SOURCE.DEEPSEEK,
  MWS_RP_TRANSLATION_SOURCE.CATALOG,
  MWS_RP_TRANSLATION_SOURCE.MANUAL,
] as const;
export type MwsRpTranslationSource = (typeof MWS_RP_TRANSLATION_SOURCES)[number];

/**
 * Переводимые поля строки RP: их уникальные китайские значения при загрузке
 * снапшота уходят в словарь mws_rp_translations (value_zh), перевод — батчем
 * по запросу (DeepSeek). Заголовок подплана (title_zh) переводится так же.
 */
export const MWS_RP_TRANSLATABLE_ROW_FIELDS = [
  'ship_type',
  'category',
  'float_state',
  'plan_kind',
  'item',
  'contractor',
  'resp_unit',
  'leader',
  'remark',
] as const;
